import {useCallback} from "react";
import {SingleValue, MultiValue, StylesConfig, GroupBase} from "react-select";
import {useTranslation} from "react-i18next";
import Creatable, {CreatableProps} from "react-select/creatable";

export type Option = {
  value?: string;
  label?: string;
};

export type CreatableSelectProps = Omit<CreatableProps<Option, boolean, GroupBase<Option>>, "isDisabled"> & {
  onCreate: (label: string) => void;
  toTop?: boolean;
  disabled?: boolean;
};

const CreatableSelect = ({
  onChange,
  onCreate,
  toTop,
  disabled,
  placeholder,
  noOptionsMessage,
  ...props
}: CreatableSelectProps) => {
  const {t} = useTranslation();

  const handleCreate = useCallback(
    (label: string) => {
      onCreate && onCreate(label);
    },
    [onCreate],
  );

  const handleChange = useCallback(
    (value: SingleValue<Option> | MultiValue<Option>) => {
      onChange && onChange(value);
    },
    [onChange],
  );

  const svgStyles = {
    color: "var(--text-color-regular)",
    svg: {
      width: 14,
      height: 14,
    },
  };

  const customStyles: StylesConfig<Option, true> = {
    control: (styles, state) => ({
      ...styles,
      borderRadius: "25px",
      padding: "4px 10px 5px",
      font: "var(--text-regular)",
      color: "var(--text-color-regular)",
      backgroundColor: "white",
      borderColor: "#cccccc",
      cursor: "pointer",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    indicatorsContainer: (styles) => ({
      ...styles,
      display: "flex",
    }),
    clearIndicator: (styles) => ({
      ...styles,
      ...svgStyles,
    }),
    dropdownIndicator: (styles) => ({
      ...styles,
      ...svgStyles,
      paddingRight: "10px",
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: "var(--color-neutral6)",
      borderRadius: "0px",
      border: "1px solid black",
      marginTop: "0px",
    }),
    option: (styles, {isSelected}) => {
      return {
        ...styles,
        font: "var(--text-regular)",
        color: isSelected ? "white" : "var(--text-color-regular)",
      };
    },
    noOptionsMessage: (styles) => ({
      ...styles,
      font: "var(--text-regular)",
    }),
    multiValue: (styles, {data}) => {
      return {
        ...styles,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "calc(100% - 7px)",
        backgroundColor: "#f4f4fca6",
        borderRadius: "7px",
        paddingTop: "3px",
        paddingBottom: "3px",
        paddingRight: "7px",
        cursor: "pointer",
      };
    },
    multiValueLabel: (styles, {data}) => ({
      ...styles,
      width: "100%",
      fontWeight: "bold",
    }),
    multiValueRemove: (styles, {data}) => ({
      ...styles,
      ":hover": {
        backgroundColor: "#ee498e",
        color: "#fff",
        borderRadius: "5px",
        cursor: "pointer",
      },
    }),
  };

  const effectivePlaceholder = placeholder ?? t("components.CreatableSelect.no");
  const effectiveNoOptionsMessage = noOptionsMessage ?? (() => t("components.CreatableSelect.noOptions"));

  return (
    <Creatable
      styles={customStyles}
      onChange={handleChange}
      onCreateOption={handleCreate}
      menuPlacement={toTop ? "top" : "bottom"}
      isClearable
      isDisabled={disabled}
      formatCreateLabel={(inputValue: string) => t("components.CreatableSelect.create", {value: inputValue})}
      {...props}
      placeholder={effectivePlaceholder}
      noOptionsMessage={effectiveNoOptionsMessage}
    />
  );
};

export default CreatableSelect;
